import { CurrencyPipe, NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  OnDestroy,
  OnInit,
  Input,
  inject,
} from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

import { PaymentCalculatorService } from '../../../services/payment-calculator.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { NgxCurrencyDirective } from 'ngx-currency';
import { NgxMatErrors } from 'ngx-mat-errors';
import { TransactionsService } from '../../../../transactions/services/transactions.service';
import { PaymentType, MultiInvoiceCheckoutData } from '../../../payment.model';
import { PaymentMethodFormData, PaymentMethodMode } from 'app/features/customer/customer.model';
import { RulesService } from '../../../../../services/profile.rules.service';
import { UserStore } from 'app/features/user/store/user.store';
import { MultiInvoiceSelectionComponent } from '../multi-invoice-selection/multi-invoice-selection.component';
import { CostOfCapitalGuardService } from 'app/services/cost-of-capital-guard.service';
import {
  InvoiceParseStatus,
  InvoiceUploadComponent,
  PendingAttachment,
} from '../invoice-upload/invoice-upload.component';
import { InvoiceAttachmentEntry } from '../../../payment.model';
import { FreelyCardService } from '../../../services/freely-card.service';
import { resolveCustomerCostOfCapital } from 'app/features/customer/utils/customer-coc.util';
import { isAdvancedFlashPayView, isMultiInvoiceModeForced } from './multi-invoice-mode';

type cap_preSelect = 'cap_other' | 'cap_1' | 'cap_2' | 'cap_3';

type InvoiceTotals = Array<InvoiceTotal>;
type InvoiceTotal = {
  name: string;
  title: string;
  value?: number;
  dependsOn?: string;
};

type InvoiceForm = FormGroup<{
  invoiceNumber: FormControl<string | null>;
  invoiceTotalAmount: FormControl<string | null>;
  taxAmount: FormControl<string | null>;
  shippingAmount: FormControl<string | null>;
  capitalAmount: FormControl<string | null>;
  capitalAutoAmount: FormControl<string | null>;
  capPreSelect: FormControl<cap_preSelect | null>;
  otherAmount: FormControl<string | null>;
  totalAmount: FormControl<string | null>;
  enablePreauth: FormControl<boolean>;
  enableTaxAmount: FormControl<boolean>;
  enableShippingAmount: FormControl<boolean>;
  enableCapitalAmount: FormControl<boolean>;
  enableOtherAmount: FormControl<boolean>;
  enableCapPreSelect: FormControl<boolean>;
  subtotalAmount: FormControl<string | null>;
  costOfCapitalPercent: FormControl<number | null>;
}>;

type InvoiceFormData = {
  invoiceNumber: string | null;
  invoiceTotalAmount: string | null;
  taxAmount: string | null;
  shippingAmount: string | null;
  capitalAmount: string | null;
  capitalAutoAmount: string | null;
  otherAmount: string | null;
  totalAmount: string | null;
};

@Component({
  selector: 'app-invoice-details',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    FormsModule,
    NgClass,
    NgIf,
    NgxMaskDirective,
    NgxCurrencyDirective,
    NgxMatErrors,
    CurrencyPipe,
    MatButtonToggleModule,
    MatButtonModule,
    MatIconModule,
    MultiInvoiceSelectionComponent,
    InvoiceUploadComponent,
  ],
  templateUrl: './invoice-details.component.html',
  host: {
    class: 'app-invoice-details',
  },
  providers: [
    provideNgxMask(),
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InvoiceDetailsComponent),
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InvoiceDetailsComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceDetailsComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Input() paymentForm!: FormControl<PaymentMethodFormData | null>;
  @Input() customerFormControl!: FormControl<any>;

  totalsConfig: InvoiceTotals = [
    { name: 'invoiceTotalAmount', title: 'Invoice Total' },
    { name: 'shippingAmount', title: 'shipping', dependsOn: 'enableShippingAmount' },
    { name: 'taxAmount', title: 'tax', dependsOn: 'enableTaxAmount' },
    { name: 'capitalAmount', title: 'capital costs', dependsOn: 'enableCapitalAmount' },
    { name: 'otherAmount', title: 'other', dependsOn: 'enableOtherAmount' },
  ];
  form: InvoiceForm = new FormGroup({
    invoiceNumber: new FormControl<string | null>('', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(100),
    ]),
    invoiceTotalAmount: new FormControl<string | null>('0', [
      Validators.required,
      Validators.min(0.01),
      (control: AbstractControl) => {
        const isAch = this.paymentForm?.getRawValue()?.paymentMode === PaymentMethodMode.achpay;
        const maxAmount =
          this.userStore?.activeProfile()?.profileDetail?.profileAdminSettings?.maxACHPayAmount ?? 100000;

        if (isAch && control.value && Number(control.value) > maxAmount) {
          // ACH max amount validator
          return { message: `Max is ${maxAmount}` };
        } else if (control.value && Number(control.value) > 185000) {
          return { message: `Max is 185000` };
        }
        return null;
      },
    ]),
    taxAmount: new FormControl<string | null>({ value: '0', disabled: true }, [
      Validators.min(0.01),
      // validate if tax amount is too big
      (control: AbstractControl) => {
        const invoiceTotalAmount = Number(this.form.controls.invoiceTotalAmount.value);
        if (this.paymentCalculatorService.isTaxAmountExceeded(invoiceTotalAmount, Number(control.value))) {
          return { message: `Max is ${this.paymentCalculatorService.getMaxTaxPercent()}%` };
        }
        return null;
      },
    ]),
    shippingAmount: new FormControl<string | null>({ value: '0', disabled: true }, [Validators.min(0.01)]),
    capitalAmount: new FormControl<string | null>({ value: '0', disabled: true }, [
      Validators.min(0.01),
      // validate if capital amount is too big
      (control: AbstractControl) => {
        const invoiceTotalAmount = Number(this.form.controls.invoiceTotalAmount.value);
        if (this.paymentCalculatorService.isCapitalAmountExceeded(invoiceTotalAmount, Number(control.value))) {
          return { message: `Max is ${this.paymentCalculatorService.getMaxCapitalPercent()}%` };
        }
        return null;
      },
    ]),
    otherAmount: new FormControl<string | null>({ value: '0', disabled: true }, [Validators.min(0.01)]),
    capitalAutoAmount: new FormControl<string | null>('0'),
    totalAmount: new FormControl<string | null>('0', [Validators.min(0.01)]),
    enablePreauth: new FormControl<boolean>(false),
    enableTaxAmount: new FormControl<boolean>(false),
    enableShippingAmount: new FormControl<boolean>(false),
    enableCapitalAmount: new FormControl<boolean>(false),
    enableOtherAmount: new FormControl<boolean>(false),
    enableCapPreSelect: new FormControl<boolean>(false),
    subtotalAmount: new FormControl<string | null>('0'),
    costOfCapitalPercent: new FormControl<number | null>(null),
    capPreSelect: new FormControl<cap_preSelect | null>(null),
  }) as InvoiceForm;

  capitalCostLabel: string = 'Capital Cost';

  protected onChange: (value: Partial<InvoiceFormData>) => void = () => {};
  protected onTouched: () => void = () => {};

  subscription = new Subscription();
  view: string | null = null;

  cardLabelInfo: string = '';

  preauthorizationAmount: number | null = null;
  refundAmount: number | null = null;
  // Refunds already applied to the transaction (Previous), and the remaining balance
  // (Available = Original − Previous). Drive both the Total-section display and the cap.
  previousRefundAmount: number = 0;
  availableRefundAmount: number | null = null;
  adjustAmount: number | null = null;

  // Tracks whether the transaction loaded for the completeAuth/refund/adjust views
  // was originally paid via ACH, since those views don't carry paymentMode through
  // the payment form (only view === 'achpay' does, which is the "new sale" path).
  isAchTransaction: boolean = false;

  private _isCompleteAuthView: boolean = false;
  private _isRefundView: boolean = false;
  private _isAdjustView: boolean = false;

  private lastAutoInvoiceNumber: string | null = null;

  private customer: any; // TODO: Replace 'any' with proper Customer type once model is available

  private paymentType: PaymentType = PaymentType.SALE;

  protected showTaxAmount = false;
  protected showShippingAmount = false;

  enableCostOfCapital: boolean = false;
  cocBlockedState: string | null = null;
  enableShippingAmount: boolean = false;
  enableTaxAmount: boolean = false;
  enableCapPreSelect: boolean = false;

  // Freely Card: when the selected card's BIN matches the Freely Card range,
  // the cost-of-capital preselect is locked to a read-only 1% "Freely Card".
  isFreelyCard: boolean = false;
  freelyCardLabel: string = 'Freely Card';
  freelyCardPercent: number = 1;

  // Make Number available to the template
  protected readonly Number = Number;

  private userStore = inject(UserStore);
  private cdRef = inject(ChangeDetectorRef);
  private freelyCardService = inject(FreelyCardService);

  /** 'upload' | 'manual' — only relevant when enableInvoiceUpload is true and view is 'flashpay' */
  invoiceInputMode: 'upload' | 'manual' = 'manual';
  pendingAttachments: InvoiceAttachmentEntry[] = [];
  isParsingInvoices = false;
  invoiceParseStatuses: InvoiceParseStatus[] = [];
  invoiceParseFailed = false;

  get enableInvoiceUpload(): boolean {
    const profile = this.userStore.activeProfile();
    return profile?.profileDetail?.profileAdminSettings?.enableInvoiceUpload ?? false;
  }

  get isFlashPayView(): boolean {
    return this.paymentType === PaymentType.FLASHPAY;
  }

  /** Advanced FlashPay — see `isAdvancedFlashPayView` in `multi-invoice-mode.ts`. */
  get isAdvancedFlashPayView(): boolean {
    return isAdvancedFlashPayView(this.userStore.activeProfile()?.profileDetail?.profileAdminSettings, this.view);
  }

  // Multi-invoice mode properties
  isMultiInvoiceMode: boolean = false;
  multiInvoiceData: MultiInvoiceCheckoutData | null = null;
  selectedCustomer: any = null;
  /** Invoice to pre-select in multi-invoice mode — the one Collect was clicked on. */
  @Input() preSelectedInvoiceId: string | null = null;
  /**
   * Open in multi-invoice mode, for a collect started from an invoice. An input rather than a
   * call from the parent after init, so the invoice list is created already holding the
   * pre-selected id and loads once.
   */
  @Input() startInMultiInvoiceMode = false;

  public isPreauthEnabledOnProfile(): boolean {
    const featureEnabled = this.userStore?.activeProfile()?.profileDetail?.profileAdminSettings?.enablePreAuth || false;
    return featureEnabled;
  }

  get isInvoiceFeatureEnabled(): boolean {
    const profile = this.userStore.activeProfile();
    return profile?.profileDetail?.profileAdminSettings?.enableInvoice ?? false;
  }

  /**
   * Free-form single / adhoc invoice entry is allowed UNLESS the profile uses
   * invoices but has adhoc auto-gen disabled — in that case only existing
   * imported invoices may be paid (multi-invoice selection mode).
   */
  get isAdhocInvoiceAllowed(): boolean {
    const s = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings;
    if (s?.enableInvoice && !s?.enableAdhocInvoiceAutoGen) {
      return false;
    }
    return true;
  }

  costOfCapitalPercentOverride: number | null = null;
  private readonly cocGuard = inject(CostOfCapitalGuardService);
  constructor(
    protected paymentCalculatorService: PaymentCalculatorService,
    protected hostElement: ElementRef,
    private route: ActivatedRoute,
    private transactionsService: TransactionsService,
    private rulesService: RulesService
  ) {
    this.form.controls.capitalAutoAmount.setValue(
      this.paymentCalculatorService.calculateCapital(0, this.costOfCapitalPercentOverride ?? undefined).toString(),
      { emitEvent: false }
    );
  }

  getCostOfCapitalPercent(): undefined | number {
    if (this.form.controls.enableCapitalAmount.value === true) {
      if (this.form.controls.capPreSelect.value === 'cap_1') {
        return this.freelyCardPercent;
      } else if (this.form.controls.capPreSelect.value === 'cap_2') {
        return 2;
      } else if (this.form.controls.capPreSelect.value === 'cap_3') {
        return 3;
      }
      return this.getCapitalPercentForCalc();
    }
    return undefined;
  }

  /** Card-brand rule percent if any, otherwise the customer/distributor effective rate. */
  private getCapitalPercentForCalc(): number {
    return this.costOfCapitalPercentOverride ?? this.getEffectiveCoc().percent;
  }

  private getEffectiveCoc() {
    const profile = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings;
    return resolveCustomerCostOfCapital(profile, this.selectedCustomer?.customerSettings);
  }

  /**
   * Re-apply customer-level CoC enable / default-selected / percent when the
   * selected customer changes. ACH / debit / state-block still force the add-on off.
   */
  private applyCustomerCocSettings(): void {
    const effective = this.getEffectiveCoc();
    this.enableCostOfCapital = effective.enabled;
    this.form.controls.enableCapitalAmount.setValue(effective.defaultSelected);
    this.setCapitalCostLabel();
    this.evaluateCocGuard(this.paymentForm?.value?.paymentMethod?.zipCode ?? undefined);
    this.cdRef.markForCheck();
  }

  setCapitalCostLabel() {
    if (this.form.controls.enableCapitalAmount.value == false) {
      this.capitalCostLabel = `Capital Cost`;
      return;
    } else if (this.isFreelyCard) {
      this.capitalCostLabel = `Capital Cost ${this.freelyCardPercent}%`;
      return;
    } else {
      const percent = this.getEffectiveCoc().percent;
      if (percent) {
        this.capitalCostLabel = `Capital Cost ${percent}%`;
      } else {
        this.capitalCostLabel = `Capital Cost`;
      }
    }
  }

  get autoPopulateInvoice(): boolean {
    const profile = this.userStore.activeProfile();
    return profile?.profileDetail?.profileAdminSettings?.autoPopulateInvoice ?? false;
  }

  private applyAutoPopulateInvoiceNumber(): void {
    if (!this.autoPopulateInvoice) return;
    if (this.isCompleteAuthView || this.isRefundView || this.isAdjustView) return;

    const current = this.form.controls.invoiceNumber.value ?? '';
    const userEdited = current !== '' && current !== this.lastAutoInvoiceNumber;
    if (userEdited) return;

    const isAch = this.paymentForm?.getRawValue()?.paymentMode === PaymentMethodMode.achpay;
    const formattedDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const next = (isAch ? 'ACH' : 'CC') + formattedDate;

    this.lastAutoInvoiceNumber = next;
    this.form.controls.invoiceNumber.setValue(next);
  }

  get enableCapPreSelectOnProfile(): boolean {
    const profile = this.userStore.activeProfile();
    return profile?.profileDetail?.profileAdminSettings?.enableCapPreSelect ?? false;
  }

  ngOnInit(): void {
    // Watch for customer changes
    if (this.customerFormControl) {
      this.subscription.add(
        this.customerFormControl.valueChanges.subscribe((customer) => {
          this.selectedCustomer = customer;
          this.applyCustomerCocSettings();
        })
      );
      // Set initial value
      this.selectedCustomer = this.customerFormControl.value;
    }

    this.route.queryParams.subscribe((params) => {
      this.view = params['view'];
      const isACCH = this.paymentForm?.getRawValue()?.paymentMode === PaymentMethodMode.achpay;
      if (isACCH) {
        this.form.controls.enableCapitalAmount.setValue(false);
        this.form.controls.enableCapPreSelect.setValue(false);
      }

      if (this.isCompleteAuthView || this.isRefundView || this.isAdjustView) {
        // Explicitly enable invoice total (authorization amount)
        this.form.controls.invoiceTotalAmount.enable({ emitEvent: false });

        // Only disable invoice number for refund or adjust view
        if (this.isRefundView || this.isAdjustView) {
          this.form.controls.invoiceNumber.disable();
        } else {
          this.form.controls.invoiceNumber.enable();
        }

        // Check toggle states and enable/disable fields accordingly
        this.updateFieldsBasedOnToggles();
      }

      // When invoices are enabled without adhoc auto-gen, free-form single-
      // invoice entry isn't permitted — force existing-invoice (multi) mode so
      // the "Single Invoice" option is never usable. The Advanced FlashPay view is
      // forced too, since it sends a selection of existing invoices.
      // `isMultiInvoiceModeForced` reads the view from the params: the refund/adjust/complete-auth
      // flags are only set further down this ngOnInit, after this first (synchronous) emission.
      if (isMultiInvoiceModeForced(this.userStore.activeProfile()?.profileDetail?.profileAdminSettings, this.view)) {
        this.switchToMultiInvoiceMode();
      }
    });

    if (this.startInMultiInvoiceMode) {
      this.switchToMultiInvoiceMode();
    }

    // Initialize all toggles to false and disable their corresponding amount controls

    this.form.controls.enablePreauth.setValue(false);
    this.form.controls.enableTaxAmount.setValue(false);
    this.form.controls.enableShippingAmount.setValue(false);
    this.form.controls.enableCapitalAmount.setValue(this.getEffectiveCoc().defaultSelected);

    this.form.controls.enableOtherAmount.setValue(false);
    this.form.controls.enableCapPreSelect.setValue(false);

    // Initially disable all amount controls
    this.form.controls.taxAmount.disable();
    this.form.controls.shippingAmount.disable();
    this.form.controls.capitalAmount.disable();
    this.form.controls.otherAmount.disable();
    this.form.controls.capPreSelect.disable();

    // Enable the toggle controls themselves
    this.form.controls.enableTaxAmount.enable();
    this.form.controls.enableShippingAmount.enable();
    this.form.controls.enableCapitalAmount.enable();
    this.form.controls.enableOtherAmount.enable();

    this.subscription.add(
      this.form.valueChanges.subscribe((result) => {
        const invoiceTotals = this.totals;

        const { subtotal, total } = this.paymentCalculatorService.calculateTotals({
          invoiceTotal: Number(this.form.controls.invoiceTotalAmount.value || 0),
          taxAmount: Number(this.form.controls.taxAmount.getRawValue() || 0),
          shippingAmount: Number(this.form.controls.shippingAmount.getRawValue() || 0),
          capitalAmount: Number(this.form.controls.capitalAmount.getRawValue() || 0),
          otherAmount: Number(this.form.controls.otherAmount.getRawValue() || 0),
        });

        this.form.controls.subtotalAmount.setValue(subtotal.toString(), { emitEvent: false });
        this.form.controls.totalAmount.setValue(total.toString(), { emitEvent: false });

        this.onChange(this.getFormValue());
      })
    );

    // Update toggle control listeners
    const toggleControls = [
      { toggle: 'enableTaxAmount', amount: 'taxAmount' },
      { toggle: 'enableShippingAmount', amount: 'shippingAmount' },
      { toggle: 'enableCapitalAmount', amount: 'capitalAmount' },
      { toggle: 'enableOtherAmount', amount: 'otherAmount' },
    ];

    toggleControls.forEach((control) => {
      this.subscription.add(
        this.form.get(control.toggle)?.valueChanges.subscribe((enabled) => {
          this.setCapitalCostLabel();
          if (enabled) {
            this.form.get(control.amount)?.enable();
            this.removeDisabledStyling(control.amount);

            // Add special handling for capital amount
            if (control.amount === 'capitalAmount') {
              const invoiceTotalAmount = Number(this.form.controls.invoiceTotalAmount.value || '0');
              const calculatedCapital = this.paymentCalculatorService.calculateCapital(
                invoiceTotalAmount,
                this.getCapitalPercentForCalc()
              );

              this.form.get(control.amount)?.setValue(calculatedCapital.toString());
            }
          } else {
            this.form.get(control.amount)?.disable();
            this.form.get(control.amount)?.setValue('0');
          }
        })
      );
    });

    this.subscription.add(
      this.form.controls.enableCapitalAmount.valueChanges.subscribe((enabled) => {
        if (enabled) {
          this.enableCapPreSelect = this.enableCapPreSelectOnProfile;

          if (this.enableCapPreSelect) {
            this.form.controls.enableCapPreSelect.setValue(true, { emitEvent: false });
            this.form.controls.capPreSelect.enable();
            if (this.isFreelyCard) {
              // Freely Card is fixed at 1% and the selection is read-only.
              this.applyFreelyCapSelection();
            } else {
              this.form.controls.capPreSelect.setValue('cap_2', { emitEvent: false });
            }
          } else {
            this.form.controls.enableCapPreSelect.setValue(false, { emitEvent: false });
            this.form.controls.capPreSelect.disable();
          }
        } else {
          this.enableCapPreSelect = false;
          this.form.controls.capPreSelect.disable();
          this.form.controls.enableCapPreSelect.setValue(false, { emitEvent: false });
        }
      })
    );

    this.subscription.add(
      this.form.controls.enablePreauth.valueChanges.subscribe((isPreauth) => {
        if (isPreauth) {
          // Disable and reset all amount-related controls
          this.form.controls.taxAmount.disable();
          this.form.controls.shippingAmount.disable();
          this.form.controls.capitalAmount.disable();
          this.form.controls.otherAmount.disable();

          // Reset toggles to false
          this.form.controls.enableTaxAmount.setValue(false, { emitEvent: false });
          this.form.controls.enableShippingAmount.setValue(false, { emitEvent: false });
          this.form.controls.enableCapitalAmount.setValue(false, { emitEvent: false });
          this.form.controls.enableOtherAmount.setValue(false, { emitEvent: false });

          // Disable the toggle controls
          this.form.controls.enableTaxAmount.disable();
          this.form.controls.enableShippingAmount.disable();
          this.form.controls.enableCapitalAmount.disable();
          this.form.controls.enableOtherAmount.disable();

          // Reset values to 0
          this.form.controls.taxAmount.setValue('0');
          this.form.controls.shippingAmount.setValue('0');
          this.form.controls.capitalAmount.setValue('0');
          this.form.controls.otherAmount.setValue('0');
        } else {
          // Enable just the toggle controls, but keep amounts disabled until their toggles are checked
          this.form.controls.enableTaxAmount.enable();
          this.form.controls.enableShippingAmount.enable();
          this.form.controls.enableCapitalAmount.enable();
          this.form.controls.enableOtherAmount.enable();
        }
      })
    );

    this.subscription.add(
      this.form.controls.capPreSelect.valueChanges.subscribe((value) => {
        if (value === 'cap_1') {
          const invoiceTotalAmount = parseFloat(this.form.controls.invoiceTotalAmount.value ?? '0') || 0;
          const capitalAutoAmount = this.paymentCalculatorService.calculateCapital(
            invoiceTotalAmount,
            this.freelyCardPercent
          );
          if (this.form.controls.capitalAutoAmount.value !== capitalAutoAmount.toString()) {
            this.form.controls.capitalAutoAmount.setValue(capitalAutoAmount, { emitEvent: false });
          }
          if (this.form.controls.capitalAmount.value !== capitalAutoAmount.toString()) {
            this.form.controls.capitalAmount.setValue(capitalAutoAmount, { emitEvent: true });
          }
        } else if (value === 'cap_2') {
          const invoiceTotalAmount = parseFloat(this.form.controls.invoiceTotalAmount.value ?? '0') || 0;
          const capitalAutoAmount = this.paymentCalculatorService.calculateCapital(invoiceTotalAmount, 2);
          if (this.form.controls.capitalAutoAmount.value !== capitalAutoAmount.toString()) {
            this.form.controls.capitalAutoAmount.setValue(capitalAutoAmount, { emitEvent: false });
          }
          if (this.form.controls.capitalAmount.value !== capitalAutoAmount.toString()) {
            this.form.controls.capitalAmount.setValue(capitalAutoAmount, { emitEvent: true });
          }
        } else if (value === 'cap_3') {
          const invoiceTotalAmount = parseFloat(this.form.controls.invoiceTotalAmount.value ?? '0') || 0;
          const capitalAutoAmount = this.paymentCalculatorService.calculateCapital(invoiceTotalAmount, 3);
          if (this.form.controls.capitalAutoAmount.value !== capitalAutoAmount.toString()) {
            this.form.controls.capitalAutoAmount.setValue(capitalAutoAmount, { emitEvent: false });
          }
          if (this.form.controls.capitalAmount.value !== capitalAutoAmount.toString()) {
            this.form.controls.capitalAmount.setValue(capitalAutoAmount, { emitEvent: true });
          }
        } else if (value === 'cap_other') {
          // Do nothing, user will enter their own value
        }
      })
    );

    this.subscription.add(
      this.form.controls.invoiceTotalAmount.valueChanges.subscribe((result) => {
        const taxAmountControl = this.form.get('taxAmount');
        const capitalAmountControl = this.form.get('capitalAmount');
        const capitalAutoAmountControl = this.form.get('capitalAutoAmount');
        const isCapitalValueChangedManually =
          capitalAutoAmountControl?.getRawValue() != capitalAmountControl?.getRawValue();

        const invoiceTotalAmount = parseFloat(result ?? '0');

        const capitalAmount = this.paymentCalculatorService.calculateCapital(
          invoiceTotalAmount,
          this.getCostOfCapitalPercent()
        );

        capitalAutoAmountControl?.setValue(capitalAmount, { emitEvent: false });
        if (!isCapitalValueChangedManually && !capitalAmountControl?.disabled) {
          capitalAmountControl?.setValue(capitalAmount, { emitEvent: true });
        }

        if (!taxAmountControl?.disabled) {
          taxAmountControl?.updateValueAndValidity();
        }

        if (!capitalAmountControl?.disabled) {
          capitalAmountControl?.updateValueAndValidity();
        }
      })
    );

    this.form.controls.capitalAmount.valueChanges.subscribe((value) => {
      if (this.form.controls.capPreSelect.enabled == true) {
        const invoiceTotalAmount = parseFloat(this.form.controls.invoiceTotalAmount.value ?? '0') || 0;
        const capitalAutoAmount = this.paymentCalculatorService.calculateCapital(
          invoiceTotalAmount,
          this.getCostOfCapitalPercent()
        );
        const capitalAmountControl = this.form.get('capitalAmount');
        const capitalAutoAmountControl = this.form.get('capitalAutoAmount');
        const capAmountEntered = parseFloat(value ?? '0') || 0;
        const calcPercent = Math.round((capAmountEntered / invoiceTotalAmount) * 100 * 100) / 100;
        const isCapitalValueChangedManually =
          capitalAutoAmountControl?.getRawValue() != capitalAmountControl?.getRawValue();

        if (isCapitalValueChangedManually && this.form.controls.capPreSelect.value !== 'cap_other') {
          this.form.controls.capPreSelect.setValue('cap_other');
        }
      }
    });

    // Check if we're in complete auth view
    this._isCompleteAuthView = this.route.snapshot.queryParams['view'] === 'completeAuth';

    if (this._isCompleteAuthView) {
      // Get transaction ID from query params
      const transactionId = this.route.snapshot.queryParams['transactionId'];
      if (transactionId) {
        this.loadPreauthorizationAmount(transactionId);
      }
    }
    // Check if we're in refund view
    this._isRefundView = this.route.snapshot.queryParams['view'] === 'refund';

    if (this._isRefundView) {
      // Get transaction ID from query params
      const transactionId = this.route.snapshot.queryParams['transactionId'];
      if (transactionId) {
        this.loadRefundFields(transactionId);
      }
    }

    // Check if we're in adjust view
    this._isAdjustView = this.route.snapshot.queryParams['view'] === 'adjust';

    if (this._isAdjustView) {
      // Get transaction ID from query params
      const transactionId = this.route.snapshot.queryParams['transactionId'];
      if (transactionId) {
        this.loadAdjustFields(transactionId);
      }
    }

    this.route.queryParams.subscribe((params) => {
      const view = params['view'];

      // Set payment type
      if (view === 'completeAuth') {
        this.paymentType = PaymentType.COMPLETE_AUTH;
      } else if (view === 'refund') {
        this.paymentType = PaymentType.REFUND;
      } else if (this.form.get('enablePreauth')?.value) {
        this.paymentType = PaymentType.PREAUTH;
      } else if (view === 'flashpay') {
        this.paymentType = PaymentType.FLASHPAY;
      } else {
        this.paymentType = PaymentType.SALE;
      }

      // Update form validation based on payment type
      this.updateFormValidation();
    });

    // Subscribe to enablePreauth changes to update validation
    this.form.get('enablePreauth')?.valueChanges.subscribe(() => {
      this.updateFormValidation();
    });

    // rule implementation for type paymentMethod.accountType and then = costOfCapitalPercent
    // Subscribe to payment form changes
    if (this.paymentForm) {
      this.subscription.add(
        this.paymentForm.valueChanges.subscribe((payment) => {
          const invoiceTotalAmount = Number(this.form.controls.invoiceTotalAmount.value || 0);

          // Get cost of capital from rules
          this.costOfCapitalPercentOverride = this.rulesService.paymentMethodToCostOfCapitalRule(
            payment?.paymentMethod?.accountType
          );

          // Calculate and update capital values
          const costOfCapitalPercent = this.paymentCalculatorService.calculateCapital(
            invoiceTotalAmount,
            this.getCapitalPercentForCalc()
          );

          this.form.patchValue(
            {
              costOfCapitalPercent: Number(costOfCapitalPercent),
              capitalAmount: costOfCapitalPercent.toString(),
            },
            { emitEvent: true }
          );

          if (!this.form.controls.enableCapitalAmount.value) {
            // Reset values when capital is disabled
            this.form.patchValue(
              {
                capitalAmount: '0',
              },
              { emitEvent: true }
            );
            return;
          }
        })
      );

      this.paymentForm.valueChanges.subscribe((payment) => {
        console.log('Payment Form Value Changed:', payment);
        this.cardLabelInfo = payment?.paymentMethod?.cardLabel ?? '';
        // Force capital-cost off unless the token is explicitly classified as
        // credit. Debit, prepaid (neither flag set), and unknown all suppress
        // the surcharge. Fall back to the cardFunding string for the
        // new-card-entry path where booleans aren't yet populated.
        const pm = payment?.paymentMethod;
        const hasBooleanClassification = pm?.isCreditCard === true || pm?.isDebitCard === true;
        const isCredit = hasBooleanClassification
          ? pm?.isCreditCard === true
          : pm?.cardFunding !== 'DEBIT' && pm?.cardFunding !== 'PREPAID';
        if (pm && !isCredit) {
          this.form.controls.enableCapitalAmount.setValue(false);
        }

        // Recognize the Freely Card by its BIN and lock the preselect to 1%.
        this.evaluateFreelyCard(pm?.cardBin, isCredit);

        // Re-evaluate state-based CoC block whenever the card changes
        const cardZip = pm?.zipCode ?? undefined;
        this.evaluateCocGuard(cardZip);

        // OnPush: mark this component dirty so the template re-evaluates the
        // [paymentMode]/[cardFunding]/[cardType] bindings passed into
        // <app-multi-invoice-selection>, which triggers its ngOnChanges and
        // shows/hides the capital cost toggle when swapping payment methods.
        this.cdRef.markForCheck();

        // Keep CC/ACH prefix in sync when payment mode changes,
        // but only if the user hasn't manually overridden the value.
        this.applyAutoPopulateInvoiceNumber();
      });
    }

    // dont show toggles if the profile setting is disabled
    const activeProfile = this.userStore.activeProfile();
    this.enableCostOfCapital = this.getEffectiveCoc().enabled;
    this.enableShippingAmount = activeProfile?.profileDetail?.profileAdminSettings?.enableShipping ?? false;
    this.enableTaxAmount = activeProfile?.profileDetail?.profileAdminSettings?.enableTax ?? false;
    //this.enableOtherAmount = activeProfile?.profileDetail?.profileAdminSettings?.enableOtherAmount ?? false;

    // Auto-populate invoice number once on init (honours the flag and skips special views)
    this.applyAutoPopulateInvoiceNumber();

    // Evaluate CoC guard on initial card (if one is already selected)
    this.evaluateCocGuard(this.paymentForm?.value?.paymentMethod?.zipCode ?? undefined);
  }

  /**
   * Recognizes the Freely Card by its BIN and, when matched on a credit card,
   * locks the cost-of-capital preselect to a read-only 1% "Freely Card".
   * Only applies when the preselect feature is enabled and the card is not in a
   * special view (refund/adjust/complete-auth/preauth) where add-ons are suppressed.
   */
  private evaluateFreelyCard(bin: string | undefined, isCredit: boolean): void {
    const inSpecialView =
      this.isCompleteAuthView || this.isRefundView || this.isAdjustView || this.form.controls.enablePreauth.value;

    if (!this.enableCapPreSelectOnProfile || inSpecialView || !this.enableCostOfCapital) {
      if (this.isFreelyCard) {
        this.clearFreelyLock();
      }
      return;
    }

    this.freelyCardService.resolve(bin).subscribe((res) => {
      if (res.isFreely && isCredit) {
        this.freelyCardPercent = res.percent;
        this.freelyCardLabel = res.label;
        this.applyFreelyLock();
      } else if (this.isFreelyCard) {
        this.clearFreelyLock();
      }
      this.cdRef.markForCheck();
    });
  }

  /**
   * Forces capital cost on (credit/Freely defaults on) and locks the preselect
   * to the read-only Freely Card 1% option.
   */
  private applyFreelyLock(): void {
    this.isFreelyCard = true;
    this.enableCapPreSelect = true;
    this.form.controls.enableCapPreSelect.setValue(true, { emitEvent: false });

    if (!this.form.controls.enableCapitalAmount.value) {
      // Enabling triggers the enableCapitalAmount handler which, seeing
      // isFreelyCard, applies the locked 1% selection.
      this.form.controls.enableCapitalAmount.setValue(true);
    } else {
      this.applyFreelyCapSelection();
    }
    this.setCapitalCostLabel();
  }

  /** Sets the preselect to the Freely Card 1% option and locks it read-only. */
  private applyFreelyCapSelection(): void {
    this.form.controls.capPreSelect.enable({ emitEvent: false });
    this.form.controls.capPreSelect.setValue('cap_1', { emitEvent: true });
    this.form.controls.capPreSelect.disable({ emitEvent: false });
  }

  /** Restores the normal editable 2%/3%/Other preselect after leaving a Freely Card. */
  private clearFreelyLock(): void {
    this.isFreelyCard = false;
    this.freelyCardLabel = 'Freely Card';
    this.freelyCardPercent = 1;

    if (this.form.controls.enableCapitalAmount.value && this.enableCapPreSelectOnProfile) {
      this.form.controls.capPreSelect.enable();
      this.form.controls.capPreSelect.setValue('cap_2', { emitEvent: true });
    }
    this.setCapitalCostLabel();
  }

  private evaluateCocGuard(cardZip: string | undefined): void {
    const effective = this.getEffectiveCoc();
    if (!effective.enabled) {
      this.enableCostOfCapital = false;
      return;
    }

    const normalizedZip = cardZip && cardZip.trim().length > 0 ? cardZip.trim() : undefined;

    this.cocGuard.checkBlocked(normalizedZip).then(({ blocked, state }) => {
      if (blocked) {
        this.enableCostOfCapital = false;
        this.cocBlockedState = state;
        this.form.controls.enableCapitalAmount.setValue(false, { emitEvent: false });
        this.form.controls.capitalAmount.setValue('0', { emitEvent: false });
      } else {
        this.enableCostOfCapital = true;
        this.cocBlockedState = null;
      }
      this.cdRef.markForCheck();
    });
  }

  private updateFieldsBasedOnToggles(): void {
    const toggleControls = [
      { toggle: 'enableTaxAmount', amount: 'taxAmount' },
      { toggle: 'enableShippingAmount', amount: 'shippingAmount' },
      { toggle: 'enableCapitalAmount', amount: 'capitalAmount' },
      { toggle: 'enableOtherAmount', amount: 'otherAmount' },
    ];

    toggleControls.forEach((control) => {
      const toggleEnabled = this.form.get(control.toggle)?.value;
      if (toggleEnabled) {
        this.form.get(control.amount)?.enable();
        this.removeDisabledStyling(control.amount);
      } else {
        this.form.get(control.amount)?.disable();
      }
    });
  }

  private removeDisabledStyling(controlName: string): void {
    setTimeout(() => {
      const field = this.hostElement.nativeElement.querySelector(`[formcontrolname="${controlName}"]`);
      if (field) {
        const formControl = field.closest('.form-control');
        if (formControl) {
          formControl.classList.remove('disabled');
        }
      }
    });
  }

  validate(control: FormControl<InvoiceForm>): ValidationErrors | null {
    // Multi-invoice mode validation
    if (this.isMultiInvoiceMode) {
      const data = this.multiInvoiceData;
      const hasInvoices = (data?.invoices?.length ?? 0) > 0;
      if (!data || (!hasInvoices && !((data.onAccountAmount ?? 0) > 0))) {
        return { required: true, message: 'Please select at least one invoice' };
      }
      // The selection's own verdict: an amount over an invoice's balance, or more on the
      // account than the customer owes.
      if (data.validationError) {
        return { invalid: true, message: data.validationError };
      }
      // Nothing to charge is fine only when credit is what covers it.
      if (data.totalAmount <= 0 && !((data.creditsAppliedAmount ?? 0) > 0)) {
        return { required: true, message: 'Total payment amount must be greater than 0' };
      }
      return null;
    }

    // Skip validation in preauth or complete auth scenarios
    const totalAmount = parseFloat(this.form.controls.totalAmount.value ?? '0');

    if (this.form.controls.enablePreauth.value || this._isCompleteAuthView) {
      if (totalAmount === 0) {
        this.form.controls.invoiceTotalAmount.setErrors({
          required: true,
          message: 'Total amount is required',
        });
        return { required: true };
      }

      if (this.isCompleteAuthView && this.preauthorizationAmount !== null) {
        if (totalAmount > this.preauthorizationAmount) {
          this.form.controls.invoiceTotalAmount.setErrors({
            totalAmountExceeded: true,
            message: 'Total amount exceeds preauthorization amount',
          });
          return { totalAmountExceeded: true };
        } else {
          this.form.controls.invoiceTotalAmount.setErrors(null);
        }
      }
    }

    if (this.isRefundView) {
      // Check if total amount is valid (greater than 0 and not exceeding refund amount)
      if (totalAmount === 0) {
        console.log('Validation failed: total amount is 0');
        return { required: true };
      }

      if (totalAmount > (this.availableRefundAmount ?? this.refundAmount ?? 0)) {
        this.form.controls.invoiceTotalAmount.setErrors({
          totalAmountExceeded: true,
          message: 'Total amount exceeds the amount available to refund',
        });
        return { totalAmountExceeded: true };
      }

      // For refund view, if we have valid amounts and invoice number is populated, consider it valid
      const invoiceNumberValue = this.form.controls.invoiceNumber.value;

      if (invoiceNumberValue && totalAmount > 0) {
        return null;
      }
    }

    if (this.form.valid) {
      return null;
    }

    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control && control.invalid && control.enabled) {
        console.log(`  ${key}: invalid, errors:`, control.errors);
      }
    });

    return {
      required: true,
    };
  }

  writeValue(data: Partial<InvoiceFormData>): void {
    this.form.patchValue(data);
  }

  registerOnChange(fn: (value: Partial<InvoiceFormData>) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  disableEnableControl(name: string, enable: boolean): void {
    // If preauth is enabled, don't allow any changes
    if (this.isPreauthEnabled) {
      return;
    }

    if (enable) {
      this.form.get(name)?.enable();
      const formControlNativeElement = this.hostElement.nativeElement.querySelector(`[formcontrolname="${name}"]`);
      if (formControlNativeElement) {
        formControlNativeElement.focus();
      }
      switch (name) {
        case 'capitalAmount':
          const formControl = this.form.get(name);
          const currentValue = formControl?.getRawValue();

          if (currentValue == 0) {
            const capitalAmount = this.form.get('capitalAutoAmount')?.getRawValue();
            formControl?.setValue(capitalAmount, { emitEvent: true });
          }
          break;
      }
    } else {
      this.form.get(name)?.disable();
    }
  }

  get totals(): InvoiceTotals {
    const invoiceTotals = [];
    for (const total of this.totalsConfig) {
      const totalControl = this.form.get(total.name);
      if (totalControl?.disabled) {
        continue;
      }
      const invoiceTotal = { ...total };
      invoiceTotal.value = parseFloat(totalControl?.getRawValue() ?? '0');
      invoiceTotals.push(invoiceTotal);
    }
    if (this.isCompleteAuthView) {
      invoiceTotals[0].title = 'Authorization Amount';
    }
    if (this.isPreauthEnabled) {
      invoiceTotals[0].title = 'Pre-Authorized Amount';
    }
    if (this.isRefundView) {
      invoiceTotals[0].title = 'Refund Amount';
    }
    if (this.isAdjustView) {
      invoiceTotals[0].title = 'Adjust Amount';
    }
    return invoiceTotals;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private enableAllControls(): void {
    // Enable all amount controls
    this.form.controls.taxAmount.enable();
    this.form.controls.shippingAmount.enable();
    this.form.controls.capitalAmount.enable();
    this.form.controls.otherAmount.enable();

    // Enable all toggles
    this.form.controls.enableTaxAmount.enable();
    this.form.controls.enableShippingAmount.enable();
    this.form.controls.enableCapitalAmount.enable();
    this.form.controls.enableOtherAmount.enable();
  }

  get isPreauthEnabled(): boolean {
    return this.form.controls.enablePreauth.value;
  }

  // Update isToggleDisabled to only consider preauth state
  isToggleDisabled(controlName: string): boolean {
    return this.isPreauthEnabled; // Only disable toggles when preauth is enabled
  }

  get isCompleteAuthView(): boolean {
    return this._isCompleteAuthView;
  }

  get isRefundView(): boolean {
    return this._isRefundView;
  }

  get isAdjustView(): boolean {
    return this._isAdjustView;
  }

  // Combines the "new sale" ACH detection (view=achpay / paymentForm.paymentMode)
  // with the historical-transaction ACH detection used by the completeAuth/refund/adjust
  // views, where the original payment method is loaded from the transaction record
  // instead of the payment form. Add-ons (Capital Cost, etc.) should never show for ACH.
  get isAchPaymentContext(): boolean {
    if (this.view === 'achpay') {
      return true;
    }
    if (this.paymentForm?.getRawValue()?.paymentMode === PaymentMethodMode.achpay) {
      return true;
    }
    return this.isAchTransaction;
  }

  // Add getter for invoice total label
  get invoiceTotalLabel(): string {
    if (this.isCompleteAuthView) {
      return 'Authorization Amount';
    }
    if (this.isRefundView) {
      return 'Refund Amount';
    }
    return this.form.get('enablePreauth')?.value ? 'Pre-Auth Amount' : 'Invoice Total';
  }

  // Add getter to control invoice number visibility
  get showInvoiceNumber(): boolean {
    return this.paymentType === PaymentType.SALE;
  }

  /**
   * Detects whether a loaded transaction (completeAuth/refund/adjust views) was
   * originally paid via ACH and, if so, suppresses card-only add-ons (Capital Cost)
   * that don't apply to ACH.
   *
   */
  private applyAchTransactionState(transaction: {
    achDetails?: unknown;
    cardDetails?: { cardType?: string | null; cardFunding?: string | null } | null;
  }): void {
    const cardType = transaction?.cardDetails?.cardType?.toString().toUpperCase();
    const cardFunding = transaction?.cardDetails?.cardFunding?.toString().toUpperCase();
    this.isAchTransaction = !!transaction?.achDetails || cardType === 'ACH' || cardFunding === 'ACH';

    if (this.isAchTransaction) {
      this.form.controls.enableCapitalAmount.setValue(false, { emitEvent: true });
      this.form.controls.enableCapPreSelect.setValue(false, { emitEvent: false });
      this.form.controls.capitalAmount.setValue('0', { emitEvent: true });
    }

    this.cdRef.markForCheck();
  }

  private loadPreauthorizationAmount(transactionId: string) {
    this.transactionsService.getTransaction(transactionId).subscribe({
      next: (transaction) => {
        //console.log('Transaction:', transaction);
        this.preauthorizationAmount = transaction.transactionResult?.authorizedAmount ?? null;

        if (this.preauthorizationAmount !== null) {
          this.form.controls.invoiceTotalAmount.setValue(
            this.paymentCalculatorService.round(this.preauthorizationAmount).toString(),
            { emitEvent: true }
          );
        }

        this.applyAchTransactionState(transaction);

        // Ensure form validity is updated after loading data
        this.form.updateValueAndValidity();
      },
      error: (error) => {
        console.error('Error loading preauthorization amount:', error);
      },
    });
  }

  private loadRefundFields(transactionId: string) {
    this.transactionsService.getTransaction(transactionId).subscribe({
      next: (transaction) => {
        // The original amount of a completed sale lives in paymentDetails (totalCharge/totalApplied).
        // transactionResult.authorizedAmount is only populated for preauths, so it was undefined for
        // a settled sale — leaving the refund amount at $0.00 and blocking the refund.
        const pd: any = transaction.paymentDetails ?? {};
        const originalAmount =
          Number(pd.totalCharge) ||
          Number(pd.totalApplied) ||
          Number(transaction.transactionResult?.authorizedAmount) ||
          0;
        this.refundAmount = originalAmount || null;

        // Account for refunds already applied so only the remaining balance can be refunded.
        // refundedAmount can arrive as a string, so coerce with Number().
        const previousRefund = Number(transaction.refundedAmount) || 0;
        this.previousRefundAmount = previousRefund;
        // round() returns a fixed-precision string; keep the numeric form for comparisons and
        // the string form for the input value.
        const availableRounded = this.paymentCalculatorService.round(
          Math.max(0, originalAmount - previousRefund)
        );
        this.availableRefundAmount = Number(availableRounded);

        if (this.refundAmount !== null) {
          // Default the refund to the AVAILABLE (remaining) amount, not the full original.
          this.form.controls.invoiceTotalAmount.setValue(availableRounded, { emitEvent: true });
        }

        const invoiceNumber = transaction.paymentDetails?.reference ?? null;

        if (invoiceNumber !== null) {
          this.form.controls.invoiceNumber.setValue(invoiceNumber, { emitEvent: true });

          // Disable the invoice number control after setting its value
          this.form.controls.invoiceNumber.disable();
        }

        this.applyAchTransactionState(transaction);

        // Update validity of all form controls
        Object.keys(this.form.controls).forEach((key) => {
          this.form.get(key)?.updateValueAndValidity({ emitEvent: false });
        });

        // Ensure form validity is updated after loading refund data
        this.form.updateValueAndValidity({ emitEvent: true });

        // Trigger onChange to notify parent form
        this.onChange(this.getFormValue());

        // Mark as touched to trigger validation
        this.onTouched();
      },
      error: (error) => {
        console.error('Error loading refund amount:', error);
      },
    });
  }

  private loadAdjustFields(transactionId: string) {
    this.transactionsService.getTransaction(transactionId).subscribe({
      next: (transaction) => {
        //console.log('Transaction:', transaction);
        this.adjustAmount = transaction.transactionResult?.authorizedAmount ?? null;
        if (this.adjustAmount !== null) {
          this.form.controls.invoiceTotalAmount.setValue(
            this.paymentCalculatorService.round(transaction.transactionResult?.authorizedAmount ?? 0).toString(),
            { emitEvent: true }
          );
        }
        const invoiceNumber = transaction.paymentDetails?.reference ?? null;
        if (invoiceNumber !== null) {
          this.form.controls.invoiceNumber.setValue(invoiceNumber, { emitEvent: true });
          // Disable the invoice number control after setting its value
          this.form.controls.invoiceNumber.disable();
        }

        this.applyAchTransactionState(transaction);

        // Ensure form validity is updated after loading adjust data
        this.form.updateValueAndValidity();
      },
      error: (error) => {
        console.error('Error loading adjust amount:', error);
      },
    });
  }

  /**
   * Single source of truth for the invoiceNumber control's validators.
   */
  private updateFormValidation(): void {
    const view = this.route.snapshot.queryParams['view'];
    const isStandalonePreauth =
      !!this.form.get('enablePreauth')?.value && view !== 'completeAuth' && view !== 'refund';

    if (isStandalonePreauth) {
      this.form.controls.invoiceNumber.clearValidators();
    } else {
      this.form.controls.invoiceNumber.setValidators([
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
      ]);
    }

    this.form.controls.invoiceNumber.updateValueAndValidity();

    // Update other validations based on payment type
    if (this.paymentType === PaymentType.COMPLETE_AUTH) {
      this.validateCompleteAuth();
    } else if (this.paymentType === PaymentType.REFUND) {
      this.validateRefund();
    } else if (this.paymentType === PaymentType.ADJUST) {
      this.validateAdjust();
    }
  }

  private validateCompleteAuth(): void {
    if (this.preauthorizationAmount !== null) {
      this.form.controls.invoiceTotalAmount.addValidators((control: AbstractControl) => {
        const value = Number(control.value);
        return value > this.preauthorizationAmount! ? { exceedsPreauth: true } : null;
      });
    }
  }

  private validateRefund(): void {
    if (this.refundAmount !== null) {
      this.form.controls.invoiceTotalAmount.addValidators((control: AbstractControl) => {
        const value = Number(control.value);
        // Cap at the remaining balance (Original − Previous refunds), not the full original.
        const cap = this.availableRefundAmount ?? this.refundAmount!;
        return value > cap ? { exceedsOriginal: true } : null;
      });
    }
  }

  private validateAdjust(): void {
    if (this.adjustAmount !== null) {
      this.form.controls.invoiceTotalAmount.addValidators((control: AbstractControl) => {
        const value = Number(control.value);
        return null;
      });
    }
  }

  getTotalAmountLabel(): string {
    if (this.isCompleteAuthView) {
      return 'Total Authorized Amount';
    } else if (this.isAdjustView) {
      return 'Total Adjust Amount';
    } else if (this.isRefundView) {
      return 'Total Refund Amount';
    } else if (this.form.get('enablePreauth')?.value) {
      return 'Requested Amount';
    } else {
      return 'Payment total';
    }
  }

  // Multi-invoice mode methods
  toggleInvoiceMode(): void {
    // Note: isMultiInvoiceMode is already updated by [(ngModel)] binding, no need to toggle it

    // Reset form data when switching modes
    if (this.isMultiInvoiceMode) {
      // Clear single invoice form but preserve preauth state
      const preauthValue = this.form.get('enablePreauth')?.value;
      this.form.reset();
      this.form.patchValue({ enablePreauth: preauthValue });
    } else {
      // Clear multi-invoice data
      this.multiInvoiceData = null;
    }

    // Emit changes
    this.onChange(this.getFormValue());
  }

  // Method to programmatically switch to multi-invoice mode
  switchToMultiInvoiceMode(): void {
    if (!this.isMultiInvoiceMode) {
      this.isMultiInvoiceMode = true;
      const preauthValue = this.form.get('enablePreauth')?.value;
      this.form.reset();
      this.form.patchValue({ enablePreauth: preauthValue });
      this.onChange(this.getFormValue());
    }
  }

  onInvoiceFilesChanged(files: PendingAttachment[]): void {
    // Any earlier extraction outcome no longer describes the current file list
    this.invoiceParseStatuses = [];
    this.invoiceParseFailed = false;

    if (files.length === 0) {
      this.pendingAttachments = [];
      this.onChange(this.getFormValue());
      return;
    }

    // Store attachment data immediately (so they persist even if parse fails)
    this.pendingAttachments = files.map((f) => ({
      filename: f.filename,
      mimeType: f.mimeType,
      base64: f.base64,
    }));

    // Call backend to extract invoice fields with AI
    this.isParsingInvoices = true;
    this.cdRef.markForCheck();
    this.transactionsService.parseInvoices(files).subscribe({
      next: (result) => {
        this.isParsingInvoices = false;
        const agg = result.aggregate;

        // Per-file outcome drives the "we couldn't read this one" warning in the upload panel
        this.invoiceParseStatuses = result.files.map((f) => ({
          filename: f.filename,
          success: f.success,
        }));

        // Collect all successfully parsed files
        const successFiles = result.files.filter((f) => f.success);

        // Sum tax and shipping across all successful files (same logic the backend uses for totalDue)
        const totalTax = successFiles.reduce((sum, f) => sum + (f.extracted.tax ?? 0), 0);
        const totalShipping = successFiles.reduce((sum, f) => sum + (f.extracted.shipping ?? 0), 0);

        // Auto-fill aggregate fields — leave them editable
        if (agg.totalDue && agg.totalDue > 0) {
          this.form.controls.invoiceTotalAmount.setValue(agg.totalDue.toFixed(2), { emitEvent: true });
        }
        // Every uploaded invoice contributes its number, e.g. "398843, 398842A"
        if (agg.suggestedInvoiceNumber) {
          this.form.controls.invoiceNumber.setValue(agg.suggestedInvoiceNumber, { emitEvent: true });
        }
        if (totalTax > 0) {
          this.form.controls.enableTaxAmount.setValue(true, { emitEvent: true });
          this.form.controls.taxAmount.setValue(totalTax.toFixed(2), { emitEvent: true });
        }
        if (totalShipping > 0) {
          this.form.controls.enableShippingAmount.setValue(true, { emitEvent: true });
          this.form.controls.shippingAmount.setValue(totalShipping.toFixed(2), { emitEvent: true });
        }

        this.cdRef.markForCheck();
        this.onChange(this.getFormValue());
      },
      error: () => {
        this.isParsingInvoices = false;
        this.invoiceParseFailed = true;
        this.cdRef.markForCheck();
        this.onChange(this.getFormValue());
      },
    });
  }

  onMultiInvoiceDataChange(data: MultiInvoiceCheckoutData | null): void {
    this.multiInvoiceData = data;
    console.log('[InvoiceDetails] Multi-invoice data changed:', data);
    const formValue = this.getFormValue();
    console.log('[InvoiceDetails] Emitting form value:', formValue);
    this.onChange(formValue);
  }

  getFormValue(): any {
    if (this.isMultiInvoiceMode) {
      // Return data in the format expected by the summary component
      const value = {
        ...this.form.getRawValue(),
        isMultiInvoiceMode: true,
        multiInvoiceData: this.multiInvoiceData,
      };
      console.log('[InvoiceDetails] getFormValue in multi-invoice mode:', value);
      return value;
    }
    const base = this.form.getRawValue();
    if (this.pendingAttachments.length > 0) {
      return { ...base, attachments: this.pendingAttachments };
    }
    return base;
  }
}
